import { Injectable } from '@angular/core'
import { LocalTimeRange, Uuid } from '@ftr/contracts/type/shared'
import { LocalDate } from '@js-joda/core'
import { Action, Selector, State, StateContext } from '@ngxs/store'
import { append, patch, removeItem, updateItem } from '@ngxs/store/operators'
import { groupCartItems } from '../../utils'
import { AddCartItems, ClearCart, RemoveCartItem, SetCourtSystemId, UpdateCartItem } from './cart.events'
import { CART_STATE_DEFAULT, CartGroup, CartItem, CartItemModel, CartStateModel } from './cart.model'

@State<CartStateModel>({
  name: 'cart',
  defaults: CART_STATE_DEFAULT,
})
@Injectable()
export class CartState {
  @Selector()
  static cartCourtSystemId(state: CartStateModel): Uuid | undefined {
    return state.courtSystemId
  }

  @Selector()
  static cartItems(state: CartStateModel): CartItem[] {
    return state.cartItems.map(mapCartItemModelToCartItem).filter(item => !!item)
  }

  @Selector()
  static numCartItems(state: CartStateModel): number {
    return CartState.cartItems(state).length
  }

  @Selector()
  static groupedCartItems(state: CartStateModel): CartGroup[] {
    const items = CartState.cartItems(state)
    return groupCartItems(items)
  }

  @Action(AddCartItems)
  addCartItems(ctx: StateContext<CartStateModel>, { items }: AddCartItems): void {
    const cartItemModels = items.map(mapCartItemToCartItemModel)
    // TODO DI-3088: Handle adding duplicate cart items (same times, date, courtroom)
    ctx.setState(patch({ cartItems: append(cartItemModels) }))
  }

  @Action(RemoveCartItem)
  removeCartItem(ctx: StateContext<CartStateModel>, { itemId }: RemoveCartItem): void {
    ctx.setState(
      patch({
        cartItems: removeItem<CartItemModel>(item => item.id === itemId),
      }),
    )
  }

  @Action(UpdateCartItem)
  updateCartItem(ctx: StateContext<CartStateModel>, { itemId, updates }: UpdateCartItem): void {
    const existingModel = ctx.getState().cartItems.find(i => i.id === itemId)
    if (!existingModel) return

    const existingItem = mapCartItemModelToCartItem(existingModel)
    if (!existingItem) return

    const cartItemModel = mapCartItemToCartItemModel({ ...existingItem, ...updates })
    ctx.setState(
      patch({
        cartItems: updateItem<CartItemModel>(existing => existing.id === itemId, cartItemModel),
      }),
    )
  }

  @Action(ClearCart)
  clearCart(ctx: StateContext<CartStateModel>): void {
    ctx.setState({ ...CART_STATE_DEFAULT })
  }

  @Action(SetCourtSystemId)
  setCourtSystemId(ctx: StateContext<CartStateModel>, { courtSystemId }: SetCourtSystemId): void {
    ctx.setState(patch<CartStateModel>({ courtSystemId }))
  }
}

function mapCartItemModelToCartItem(cartItemModel: CartItemModel): CartItem | undefined {
  try {
    return {
      ...cartItemModel,
      recordingDate: LocalDate.parse(cartItemModel.recordingDate),
      sessionTime: LocalTimeRange.parse(cartItemModel.sessionTime),
      requestedTimes: cartItemModel.requestedTimes.map(timeframe => LocalTimeRange.parse(timeframe)),
    }
  } catch {
    // TODO DI-3088: Handle stale & corrupt data (need to also remove corrupt items from state / store)
    return undefined
  }
}

function mapCartItemToCartItemModel(cartItem: CartItem): CartItemModel {
  return {
    ...cartItem,
    recordingDate: cartItem.recordingDate.toString(),
    sessionTime: cartItem.sessionTime.toString(),
    requestedTimes: cartItem.requestedTimes.map(timeframe => timeframe.toString()),
  }
}
